import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { endpoints } from '@rossum/api-client';
import { Runs } from '@rossum/api-client/hooks';
import { api } from '../../../../../lib/apiClient';
import { notify } from '../../../../toaster';
import { QUERY_KEY_RUNS } from './useRuns';

const CANCEL_RUNNING_JOB_MUTATION_KEY = 'cancel-running-job';

export const useCancelRun = () => {
  const queryClient = useQueryClient();
  const intl = useIntl();

  return useMutation({
    mutationKey: [CANCEL_RUNNING_JOB_MUTATION_KEY],
    mutationFn: ({ runUuid }: { runUuid: string }) =>
      api.request(endpoints.hooks.cancelRun(runUuid)),
    onError: () => {
      notify.error({
        title: intl.formatMessage({
          id: 'features.activities.cancelActivity.error.title',
        }),
      });
    },
    onSuccess: async (_response, { runUuid }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [QUERY_KEY_RUNS] });

      // The optimistic update sets cancelRequested locally to put the run job in `isCancelling` state immediately.
      // The backend cancel endpoint is fire-and-forget — the real timestamp arrives later via the runs list poll.
      // This, in turn, increases refetch frequency until the cancellation is resolved.
      const cancelRequested = new Date().toISOString();

      queryClient.setQueriesData<Runs | undefined>(
        { queryKey: [QUERY_KEY_RUNS] },
        prev => {
          const index = prev?.results.findIndex(r => r.uuid === runUuid);
          const run = prev?.results[index ?? -1];

          if (index !== undefined && run && prev?.results) {
            const updatedState = {
              ...prev,
              results: [
                ...prev.results.slice(0, index),
                {
                  ...run,
                  cancelRequested,
                },
                ...prev.results.slice(index + 1),
              ],
            };

            return updatedState;
          }

          return prev;
        }
      );

      notify.default({
        title: intl.formatMessage({
          id: 'features.activities.cancelActivity.success.title',
        }),
      });
    },
  });
};
